import { Button, Typography } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ErrorBox, FlexBox } from 'components/Box'
import { BoxForm, NumberField, Switch, TextField } from 'components/Form'
import { Dialog } from 'features/globalSetup/pages/GoogleAdManagerConnectionPage/sections/SetupPrebidStackSection/components/Dialog'
import { GamSetupOrderFreshStatusAndError } from 'features/globalSetup/pages/GoogleAdManagerConnectionPage/sections/SetupPrebidStackSection/hooks/useGetFreshStatusAndErrorForGamSetupOrders'
import { GamStatus } from 'features/globalSetup/types/gamStatus'
import { useAccessibility } from '../hooks/useAccessibility'
import { StatusBadge } from '../StatusBadge'
import { CommonProps } from '../types'
import { Schema } from './schema'

export const Form = ({
  formId,
  gamSetupOrder,
  getFreshStatusAndError,
  isDialogOpen,
  setIsDialogOpen,
}: CommonProps): JSX.Element => {
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'googleAdManagerConnection.setupPrebidStack',
  })
  const { control } = useFormContext<Schema>()

  const freshData: GamSetupOrderFreshStatusAndError | null = getFreshStatusAndError(
    gamSetupOrder?.id,
  )
  const status: GamStatus | undefined = freshData?.status ?? gamSetupOrder?.status
  const errorMessage: string | undefined = freshData?.errorMessage ?? gamSetupOrder?.errorMessage

  const { isActionDisabled, isSaveButtonDisabled, isSaveButtonVisible } = useAccessibility<Schema>({
    status,
  })

  return (
    <BoxForm>
      <FlexBox
        axis='x'
        sx={{ gap: 1 }}
      >
        <Typography>{t('form.instream.header')}</Typography>
        <StatusBadge
          gamSetupOrder={gamSetupOrder}
          status={status}
        />
      </FlexBox>

      <TextField
        control={control}
        disabled={isActionDisabled}
        label={t('form.instream.vastUrl.label')}
        name='vastUrl'
        tooltip={{
          content: t('form.instream.vastUrl.tooltip'),
        }}
      />

      <NumberField
        control={control}
        disabled={isActionDisabled}
        label={t('form.instream.duration.label')}
        name='duration'
        tooltip={{
          content: t('form.instream.duration.tooltip'),
        }}
      />

      <Switch
        control={control}
        disabled={isActionDisabled}
        label={t('form.instream.jsdelivrEnabled.label')}
        labelPlacement='start'
        name='jsdelivrEnabled'
        tooltip={{
          content: t('form.instream.jsdelivrEnabled.tooltip'),
        }}
      />

      {status === 'failed' && errorMessage !== undefined && (
        <ErrorBox errorMessage={errorMessage} />
      )}

      {isSaveButtonVisible && (
        <Button
          disabled={isSaveButtonDisabled}
          onClick={(): void => setIsDialogOpen(true)}
          sx={{ width: 'fit-content' }}
          variant='contained'
        >
          {t('form.start.label')}
        </Button>
      )}

      <Dialog<Schema>
        formId={formId}
        gamSetupOrder={gamSetupOrder}
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
      />
    </BoxForm>
  )
}
